import type { PieLabelRenderProps, LegendPayload } from 'recharts';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { formatReais } from '@/lib/utils';
import { EmptyState } from '@mustauto/ui/web';
import { CircleDollarSign } from 'lucide-react';

export interface RevenueDonutProps {
  parts: number;
  labor: number;
  outsourced: number;
}

const COLORS = [
  'hsl(217, 91%, 60%)',  // blue
  'hsl(142, 71%, 45%)',  // green
  'hsl(263, 70%, 55%)',  // purple
];

function renderCustomLabel(props: PieLabelRenderProps) {
  const cx = Number(props.cx ?? 0);
  const cy = Number(props.cy ?? 0);
  const midAngle = Number(props.midAngle ?? 0);
  const innerRadius = Number(props.innerRadius ?? 0);
  const outerRadius = Number(props.outerRadius ?? 0);
  const percent = Number(props.percent ?? 0);

  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="currentColor"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs fill-foreground"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export function RevenueDonut({ parts, labor, outsourced }: RevenueDonutProps) {
  const total = parts + labor + outsourced;

  if (total === 0) {
    return (
      <EmptyState
        compact
        icon={<CircleDollarSign size={22} />}
        title="Sem dados de receita no período"
        description="A composição entre peças, mão de obra e terceirizados aparece quando houver OS faturada."
      />
    );
  }

  const data = [
    { name: 'Pecas', value: parts },
    { name: 'Mao de Obra', value: labor },
    { name: 'Terceirizados', value: outsourced },
  ].filter((d) => d.value > 0);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="45%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={3}
          dataKey="value"
          label={renderCustomLabel}
          labelLine={false}
        >
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value, name) => [formatReais(Number(value)), String(name)]}
          contentStyle={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            borderRadius: '0.5rem',
            color: 'var(--color-foreground)',
          }}
        />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          formatter={(value: string, entry: LegendPayload) => {
            const payload = entry.payload as { value?: number } | undefined;
            if (!payload || payload.value == null) return value;
            return `${value} — ${formatReais(payload.value)}`;
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
